import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { DatePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
    ViewEncapsulation,
} from '@angular/core';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { NotificationsService } from 'app/layout/common/notifications/notifications.service';
import { ActionItem, Notification } from 'app/layout/common/notifications/notifications.types';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'notifications',
    templateUrl: './notifications.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'notifications',
    imports: [
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        MatSnackBarModule,
        NgClass,
        NgTemplateOutlet,
        DatePipe,
    ],
})
export class NotificationsComponent implements OnInit, OnDestroy {
    @ViewChild('notificationsOrigin') private _notificationsOrigin: MatButton;
    @ViewChild('notificationsPanel') private _notificationsPanel: TemplateRef<any>;
    @ViewChild('actionsOrigin') private _actionsOrigin: MatButton;
    @ViewChild('actionsPanel') private _actionsPanel: TemplateRef<any>;

    notifications: Notification[];
    unreadCount: number = 0;
    /** #484: approvals waiting for this user (overdue first); no read state, they clear when actioned. */
    actions: ActionItem[] = [];
    overdueCount = 0;
    /** #484 switch (feature.notification_actions): the Actions icon shows only when on. */
    actionsEnabled = false;

    private _overlayRef: OverlayRef;
    private _actionsOverlayRef: OverlayRef;
    private _unsubscribeAll = new Subject<void>();

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _notificationsService: NotificationsService,
        private _overlay: Overlay,
        private _viewContainerRef: ViewContainerRef,
        private _router: Router,
        private _snackBar: MatSnackBar
    ) {}

    ngOnInit(): void {
        this._notificationsService.notifications$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((notifications: Notification[]) => {
                this.notifications = notifications;
                this._changeDetectorRef.markForCheck();
            });

        this._notificationsService.unreadCount$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((count: number) => {
                this.unreadCount = count;
                this._changeDetectorRef.markForCheck();
            });

        this._notificationsService.newNotification$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((notification: Notification) => this._showPopup(notification));

        this._notificationsService.actionsEnabled$.pipe(takeUntil(this._unsubscribeAll)).subscribe((on) => {
            this.actionsEnabled = on;
            this._changeDetectorRef.markForCheck();
        });

        this._notificationsService.actions$.pipe(takeUntil(this._unsubscribeAll)).subscribe((actions) => {
            this.actions = actions;
            this.overdueCount = actions.filter((a) => a.overdue).length;
            this._changeDetectorRef.markForCheck();
        });

        this._notificationsService.newActions$.pipe(takeUntil(this._unsubscribeAll)).subscribe((fresh) => {
            const one = fresh.length === 1 ? fresh[0] : null;
            const ref = this._snackBar.open(
                one ? `Action needed: ${one.title}` : `${fresh.length} new actions need you`,
                'Open',
                {
                    duration: 20_000,
                    horizontalPosition: 'end',
                    verticalPosition: 'top',
                }
            );
            ref.onAction().subscribe(() => (one ? this.openAction(one) : this.openActionsPanel()));
        });
    }

    openActionsPanel(): void {
        if (!this._actionsPanel || !this._actionsOrigin) return;
        this._actionsOverlayRef ??= this._createOverlayFor(this._actionsOrigin);
        this._actionsOverlayRef.attach(new TemplatePortal(this._actionsPanel, this._viewContainerRef));
        this._notificationsService.syncActions();
    }

    closeActionsPanel(): void {
        this._actionsOverlayRef?.detach();
    }

    /** Opens the record; the action stays until the approval is done there. */
    openAction(action: ActionItem): void {
        this._router.navigateByUrl(action.link);
        this.closeActionsPanel();
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
        this._overlayRef?.dispose();
        this._actionsOverlayRef?.dispose();
    }

    openPanel(): void {
        if (!this._notificationsPanel || !this._notificationsOrigin) return;
        this._overlayRef ??= this._createOverlayFor(this._notificationsOrigin);
        this._overlayRef.attach(new TemplatePortal(this._notificationsPanel, this._viewContainerRef));
        this._notificationsService.sync(true).subscribe(); // re-sync read / cleared state of the visible list
    }

    closePanel(): void {
        this._overlayRef?.detach();
    }

    markAllAsRead(): void {
        this._notificationsService.markAllAsRead();
    }

    markRead(notification: Notification): void {
        this._notificationsService.markRead(notification);
    }

    delete(notification: Notification): void {
        this._notificationsService.delete(notification);
    }

    /** Mark read, then open the link (FE-22: the email on the Team dashboard / My mail). */
    open(notification: Notification): void {
        this._notificationsService.markRead(notification);
        if (notification.link) this._router.navigateByUrl(notification.link);
        this.closePanel();
    }

    private _showPopup(notification: Notification): void {
        const ref = this._snackBar.open(
            notification.title || 'New notification',
            notification.link ? 'View' : 'Dismiss',
            {
                duration: 12_000,
                horizontalPosition: 'end',
                verticalPosition: 'bottom',
            }
        );
        if (notification.link) ref.onAction().subscribe(() => this.open(notification));
    }

    private _createOverlayFor(origin: MatButton): OverlayRef {
        const ref = this._overlay.create({
            hasBackdrop: true,
            backdropClass: 'fuse-backdrop-on-mobile',
            scrollStrategy: this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay
                .position()
                .flexibleConnectedTo(origin._elementRef.nativeElement)
                .withLockedPosition(true)
                .withPush(true)
                .withPositions([
                    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
                    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
                    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
                    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom' },
                ]),
        });
        ref.backdropClick().subscribe(() => ref.detach());
        return ref;
    }
}
